import * as Sentry from '@sentry/react';
import { sentryConfig } from 'config/sentry';
import { replayPrivacy } from './replayPrivacy';

export interface MonitoringContext {
  readonly userId: string | null;
  readonly tenantId: string | null;
  readonly tenantName: string | null;
  readonly projectId: string | null;
  readonly replayAllowed: boolean;
}

const anonymousContext: MonitoringContext = {
  userId: null,
  tenantId: null,
  tenantName: null,
  projectId: null,
  replayAllowed: false,
};

// The SDK belongs to the browser tab, not a component's mount/unmount lifecycle.
let currentContext: MonitoringContext | undefined;
let requestedContext: MonitoringContext = anonymousContext;
let transitioningReplay = false;
let boundaryRevision = 0;

type Identity = Pick<MonitoringContext, 'userId' | 'tenantId'>;

function hasSameIdentity(left: Identity, right: Identity) {
  return left.userId === right.userId && left.tenantId === right.tenantId;
}

function hasSameReplayBoundary(left: MonitoringContext, right: MonitoringContext) {
  return hasSameIdentity(left, right) && left.replayAllowed === right.replayAllowed;
}

function updateContext(context: MonitoringContext) {
  const previous = currentContext;
  const userChanged = !previous || previous.userId !== context.userId;
  const tagsChanged =
    !previous ||
    previous.tenantId !== context.tenantId ||
    previous.tenantName !== context.tenantName ||
    previous.projectId !== context.projectId;

  currentContext = context;
  if (!userChanged && !tagsChanged) return;

  if (previous && hasSameIdentity(previous, context) && previous.projectId !== context.projectId) {
    Sentry.addBreadcrumb({
      category: 'project',
      message: 'Project changed',
      data: { from: previous.projectId, to: context.projectId },
    });
  }

  if (userChanged) Sentry.setUser(context.userId ? { id: context.userId } : null);
  if (tagsChanged) {
    Sentry.setTags({
      tenantId: context.tenantId ?? undefined,
      tenantName: context.tenantName ?? undefined,
      projectId: context.projectId ?? undefined,
    });
  }
}

function resetContext() {
  const hadIdentity = currentContext?.userId != null || currentContext?.tenantId != null;
  updateContext(anonymousContext);
  if (hadIdentity) Sentry.getCurrentScope().clearBreadcrumbs();
}

async function startSampledReplay(replay: NonNullable<ReturnType<typeof Sentry.getReplay>>) {
  const recordSession = Math.random() < sentryConfig.sessionSampleRate;
  if (!recordSession && sentryConfig.errorSampleRate === 0) return;

  replay.startBuffering();
  if (recordSession) {
    // Sentry 7's start() does not reset a previous buffer's recording mode.
    // flush() is the public API for promoting a buffer into continuous recording.
    await replay.flush();
  }
  // Otherwise the SDK applies error sampling when an error occurs in buffer mode.
}

export function syncSentryMonitoring(context: MonitoringContext): void {
  if (!sentryConfig.enabled) return;
  if (!hasSameReplayBoundary(requestedContext, context)) boundaryRevision++;
  // Keep an owned snapshot so callers cannot mutate an in-flight transition.
  requestedContext = { ...context };
  const identityChanged = currentContext !== undefined && !hasSameIdentity(currentContext, context);

  const boundaryChanged = currentContext
    ? !hasSameReplayBoundary(currentContext, context)
    : !context.replayAllowed;
  const replay = boundaryChanged && sentryConfig.replayEnabled ? Sentry.getReplay() : undefined;
  // A promotion may still be recording while its upload is pending. Discard the
  // unsent tail so it cannot contain activity from the next identity or auth page.
  if (replay) replayPrivacy.blockReplay(replay.getReplayId());

  if (transitioningReplay) {
    // Same-account project changes can update immediately, including breadcrumbs.
    // New identities stay anonymous until the old recording has stopped.
    if (identityChanged) resetContext();
    else updateContext(requestedContext);
    return;
  }

  if (!boundaryChanged) {
    updateContext(requestedContext);
    return;
  }

  resetContext();
  if (!replay) {
    updateContext(requestedContext);
    return;
  }

  transitioningReplay = true;
  void transitionReplay(replay);
}

async function transitionReplay(replay: NonNullable<ReturnType<typeof Sentry.getReplay>>) {
  try {
    let replayRevision: number;
    do {
      await replay.stop();
      replayRevision = boundaryRevision;
      updateContext(requestedContext);
      if (requestedContext.replayAllowed) await startSampledReplay(replay);
      // Even A -> B -> A must end the old replay. Compare revisions so returning
      // to the same identity cannot hide an intervening account/tenant switch
      // or a visit to a sensitive authentication route.
      // Finish the SDK's promotion before stopping again to avoid a late restart.
    } while (replayRevision !== boundaryRevision);
  } catch (error) {
    replayPrivacy.blockReplay(replay.getReplayId());
    // Best effort cleanup if starting/promoting the new recorder failed.
    try {
      await replay.stop();
    } catch {
      // Its uploads remain blocked even if the SDK cannot stop it.
    }
    updateContext(requestedContext);
    // Monitoring must never prevent authentication or cause an unhandled rejection.
    console.warn('Unable to transition Sentry replay', error);
  } finally {
    transitioningReplay = false;
  }
}

/** Suspend recording before auth state changes; the hook resumes it on a safe route. */
export function clearSentryMonitoring(nextIdentity?: Identity): void {
  // Compare the requested destination: currentContext is temporarily anonymous
  // while stopping the old replay. A repeated login must not cancel that switch,
  // and unchanged hook inputs will not trigger another synchronization effect.
  if (nextIdentity && hasSameIdentity(requestedContext, nextIdentity)) return;
  syncSentryMonitoring(anonymousContext);
}
